import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Actions } from 'actions'

import { changeRecommendedProgramStatus } from 'api/actions/recommended/recommendedActions'
import {
    ChangeRecommendedProgramStatusPayload,
    ChangeRecommendedProgramStatusResponse,
    InfiniteGetRecommendedProgramsListResponseResponse,
    RecommendedProgram,
} from 'api/actions/recommended/recommendedActions.types'
import { Mutations } from 'api/actions/recommended/mutations.enum'
import { Queries } from 'api/actions/recommended/queries.enum'

export const useChangeRecommendedProgramStatus = () => {
    const dispatch = useDispatch()
    const queryClient = useQueryClient()

    const mutation = useMutation<
        ChangeRecommendedProgramStatusResponse,
        AxiosError,
        ChangeRecommendedProgramStatusPayload,
        {
            previousPrograms?: InfiniteGetRecommendedProgramsListResponseResponse
        }
    >({
        mutationKey: [Mutations.changeRecommendedProgramStatus],
        mutationFn: changeRecommendedProgramStatus,
        onMutate: async ({ id, is_active: isActive }) => {
            await queryClient.cancelQueries([Queries.getRecommendedProgramsList])

            const previousPrograms = queryClient.getQueryData<InfiniteGetRecommendedProgramsListResponseResponse>([
                Queries.getRecommendedProgramsList,
            ])

            const updateRecommendedProgram = (recommendedProgram: RecommendedProgram): RecommendedProgram =>
                recommendedProgram.companySolution?.id === id
                    ? {
                          ...recommendedProgram,
                          companySolution: {
                              id: recommendedProgram.companySolution?.id || id,
                              isActive,
                          },
                      }
                    : recommendedProgram

            if (previousPrograms) {
                queryClient.setQueryData([Queries.getRecommendedProgramsList], {
                    ...previousPrograms,
                    pages: previousPrograms.pages.map((previousPrograms) => ({
                        ...previousPrograms,
                        results: previousPrograms.results.map(updateRecommendedProgram),
                    })),
                })
            }

            return { previousPrograms }
        },
        onError: (err, newProgram, context) => {
            queryClient.setQueryData([Queries.getRecommendedProgramsList], context?.previousPrograms)
        },
        onSuccess: async ({ isArchived, isActive }) => {
            await queryClient.invalidateQueries([Queries.getRecommendedProgramsList])

            dispatch(
                Actions.showToast({
                    type: isArchived ? 'warning' : isActive ? 'success' : 'warning',
                    message: `Program has been ${isArchived ? 'archived' : isActive ? 'activated' : 'deactivated'}!`,
                }),
            )
        },
    })

    return mutation
}
